import {Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {first, startWith, takeUntil} from 'rxjs/operators';
import {ListResponse} from '../../interface/response/list-response';
import {MatSelectChange} from '@angular/material/select';
import {User} from '../../interface/user';
import {UserService} from '../../services/user.service';
import {ReplaySubject} from 'rxjs';
import {Partner} from "../../interface/partner";
import {CountedAndPaginated} from "../../interface/response/counted-and-paginated";
import {InsuranceCompany} from "../../interface/insurance-company";
import {AuthService} from "../../services/auth.service";
import {UserPermissions} from "../../interface/user-permissions";

@Component({
  selector: 'app-dict-users',
  templateUrl: './dict-users.component.html',
  styleUrls: ['./dict-users.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictUsersComponent)}
  ]
})
export class DictUsersComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() userFormsPermission = false;
  @Input() userNames: string[];
  @Input() role = false;
  @Input() userPermissionsEditable = false;
  @Output() selectedUser: EventEmitter<User> = new EventEmitter<User>();
  @Output() selectedUsers: EventEmitter<User[]> = new EventEmitter<User[]>();
  @Input() userFilter: string = null;
  @Input() userPageSize: number;
  @Input() filterEmployeeOnly = false;
  @Input() filterActiveOnly = false;
  @Input() filterDynamicForm = false;


  pageSize: number;
  active = true;
  externalControl = true;
  disabled = false;
  items: User[] = [];
  value?: number = null;
  usersFilterCtrl: FormControl;
  itemsSubject: ReplaySubject<ListResponse<User>> = new ReplaySubject<ListResponse<User>>(1);
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
      private userService: UserService,
      private fb: FormBuilder,
      private authService: AuthService,
  ) {
    this.userPermissions = authService.getUserPermissions();
    this.userPermissions.subscribe(userPermissions => {
      if (userPermissions.user.read) {

        if (!this.userFormsPermission){
          this.usersFilterCtrl = fb.control(this.userNames);
        } else {
          this.usersFilterCtrl = fb.control('');
        }
      } else {
        this.active = false;
      }
    });
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (this.active) {

      if (this.filterEmployeeOnly) {
        this.userService.loadEmployeesNotCountedList(0, 999, {}, this.itemsSubject);
      } else {
        if (this.filterDynamicForm){
          this.userService.loadNotCountedList(0, 999, {active: 1, roleNot: 'ROLE_ASF360'}, this.itemsSubject);
        } else {
          this.userService.loadNotCountedList(0, 999, {}, this.itemsSubject);
        }
      }

      if (this.userFormsPermission) {
        this.disabled = true;
      }
      this.itemsSubject
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: ListResponse<User>) => {
          this.items = response.items;
          if (this.userFilter) {
            if (this.userFormsPermission) {
              this.items = this.items.filter(item => item.roles && !item.roles.includes(this.userFilter));
            } else {
              this.items = this.items.filter(item => item.roles && item.roles.includes(this.userFilter));
            }
          }
          if (this.filterEmployeeOnly) {
            this.items = this.items.filter(item => item.employee === true);
          }
          if (this.filterActiveOnly) {
            this.items = this.items.filter(item => item.active);
          }
        });

      if (this.userPageSize) {
        this.pageSize = this.userPageSize;
      } else {
        this.pageSize = 5;
      }

      this.usersFilterCtrl.valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe((val: string) => {
          if (this.userFormsPermission) {
            if (this.filterEmployeeOnly) {
              this.userService.loadEmployeesNotCountedList(0, 999, {context: val}, this.itemsSubject);
            } else {
              if (this.filterDynamicForm){
                this.userService.loadNotCountedList(0, 999, {context: val, active: 1, roleNot: 'ROLE_ASF360'}, this.itemsSubject);
              } else {
                this.userService.loadNotCountedList(0, 999, {context: val}, this.itemsSubject);
              }
            }
          } else if (val) {
            if (this.filterEmployeeOnly) {
              this.userService.loadEmployeesNotCountedList(0, 999, {context: val}, this.itemsSubject);
            } else {
              this.userService.loadNotCountedList(0, 999, {context: val}, this.itemsSubject);
            }
          } else if (this.control.value) {
            if (this.filterEmployeeOnly) {
              this.userService.loadEmployeesNotCountedList(0, 999, {uuid: this.control.value}, this.itemsSubject);
            } else {
              this.userService.loadNotCountedList(0, 999, {uuid: this.control.value}, this.itemsSubject);
            }
          }
        });

      if (this.control === null) {
        this.control = new FormControl('');
        this.externalControl = false;
      }

      this.control.valueChanges
        .pipe(
          startWith(this.control.value),
          takeUntil(this.destroyed$)
        )
        .subscribe((val: string) => {
          if (val) {
            let exists = false;
            this.items.forEach((user: User) => {
              if (user.uuid === this.control?.value) {
                this.selectedUser.emit(user);
              }

              if (user.uuid === val) {
                exists = true;
              }
            });

            if (!exists) {
              if (this.userFormsPermission) {
                return;
              } else {
                if (this.filterEmployeeOnly) {
                  this.userService.loadEmployeesNotCountedList(0, 999, {
                    uuid: val
                  }, this.itemsSubject);
                } else {
                  this.userService.loadNotCountedList(0, 999, {
                    uuid: val
                  }, this.itemsSubject);
                }
              }
            }
          }
        });

      this.itemsSubject
        .pipe(takeUntil(this.destroyed$))
        .subscribe((result: ListResponse<User>) => {
          this.items = result.items;

          if (this.filterEmployeeOnly) {
            this.items = this.items.filter(item => item.employee === true);
          }
          if (this.filterActiveOnly) {
            this.items = this.items.filter(item => item.active);
          }

          if (this.multiple) {
            const selected = [];
            this.items.forEach((user: User) => {
              if (this.control.value?.includes(user.uuid)) {
                selected.push(user);
              }

              this.selectedUsers.emit(selected);
            });
          } else {
            this.items.forEach((user: User) => {
              if (user.uuid === this.control.value) {
                this.selectedUser.emit(user);
              }
            });
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }
    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }

  roleType(user: User): string {
    if (user.roles.includes('ROLE_ASF360')) {
      return 'ASF360';
    }
    if (user.roles.includes('ROLE_ADMIN')) {
      return 'Admin';
    }
    if (user.roles.includes('ROLE_USER')) {
      return 'Pracownik';
    }
    return 'Unknown';
  }
}
